import React, { useState, useEffect, useRef } from 'react';

// Client search select component
const ClientSearchSelect = ({ clients, value, onChange, className }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredClients, setFilteredClients] = useState(clients || []);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const dropdownRef = useRef(null);
  
  // Get the selected client display text
  const selectedClient = clients.find(client => client._id === value);
  const displayText = selectedClient 
    ? `${selectedClient.name} ${selectedClient.email ? `(${selectedClient.email})` : ''}`
    : '-- Select Client --';
  
  // Sort clients alphabetically
  const sortClients = (clientsToSort, order) => {
    return [...clientsToSort].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      
      if (order === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
  };
  
  // Filter and sort clients when search term or sort order changes
  useEffect(() => {
    let filtered;
    
    if (!searchTerm.trim()) {
      filtered = clients || [];
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = (clients || []).filter(client => 
        client.name?.toLowerCase().includes(searchTermLower) || 
        client.email?.toLowerCase().includes(searchTermLower) ||
        client.cellPhone?.includes(searchTerm)
      );
    }
    
    // Apply sorting
    const sorted = sortClients(filtered, sortOrder);
    setFilteredClients(sorted);
  }, [searchTerm, clients, sortOrder]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle selection and close dropdown
  const handleSelect = (clientId) => {
    onChange(clientId);
    setIsOpen(false);
    setSearchTerm('');
  };
  
  // Clear selection
  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className={`${className} cursor-pointer flex justify-between items-center`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="truncate">{displayText}</div>
        <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
        </svg>
      </div>
      
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b">
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md mb-2"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Sort:</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortOrder('asc');
                }}
                className={`px-2 py-1 text-xs rounded ${
                  sortOrder === 'asc' 
                    ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                A-Z
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortOrder('desc');
                }}
                className={`px-2 py-1 text-xs rounded ${
                  sortOrder === 'desc' 
                    ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Z-A
              </button>
            </div>
          </div>
          
          <div className="py-1">
            <div 
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <span className="font-medium text-gray-500">Clear Selection</span>
            </div>
            
            {filteredClients.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500">No clients found</div>
            ) : (
              filteredClients.map(client => (
                <div
                  key={client._id}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelect(client._id)}
                >
                  {client.name} {client.email && <span className="text-gray-500">({client.email})</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSearchSelect; 